import { GoogleMapsOverlay as DeckOverlay } from '@deck.gl/google-maps';
import { Layer } from '@deck.gl/layers';
import { ScatterplotLayer } from '@deck.gl/layers';
import { GOOGLE_MAP_KEY } from '@env';
import { cloneDeep } from 'lodash';
import { Subject, forkJoin, of } from 'rxjs';
import { debounceTime, filter, map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import tinycolor from 'tinycolor2';

import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';

import { MxEventName, MxPropertyName } from '@axon-enterprise/track';
import { SnCurrentOrganization } from '@lib/auth';
import { FsFormatDatePipe } from '@lib/common/pipes';
import {
  GoogleMap,
  GoogleMapLayer,
  GoogleMapMarker,
  GoogleMapMarkerType,
  GoogleMapPopup,
} from '@lib/geo/google/map';
import { MapStoreService } from '@lib/geo/map';
import { MixpanelService } from '@lib/mixpanel';
import { t } from '@lib/translation';
import { THEME } from '@lib/ui/theme';

import {
  FsLprCamerasService,
  IFsLprResult,
  LprQueryResultsModel,
  LprReadsAPI,
  SnPlate,
} from '../../api';
import { LprIntegrationsService } from '../../services/lpr-integrations.service';
import { PlatesService } from '../../services/plates.service';
import { LprCameraDetailsPopupComponent } from '../lpr-camera-details-popup/lpr-camera-details-popup.component';
import {
  FsLprQueryCameraAdvancedMarker,
  FsLprCameraAdvancedMarker,
  FsLprCameraWebGlMarkerLayer,
  FsLprCameraWebGlScatterPlotLayer,
} from '../map';
import { PlateInfoPopupComponent } from '../plate-info-popup/plate-info-popup.component';
import { SnPlateMarker } from '../plate-marker/plate-marker';

// DeckGL tooltip styles
export const TOOLTIP_STYLES = {
  position: 'absolute',
  pointerEvents: 'none',
  zIndex: '9999',
  background: 'rgb(41, 50, 60)',
  color: 'rgb(160, 167, 180)',
  padding: '10px',
  fontSize: '11px',
  lineHeight: '1.2',
  maxWidth: '70px',
  whiteSpace: 'normal',
  boxShadow: 'rgba(0, 0, 0, 0.3) 0px 2px 6px',
  textAlign: 'left',
  fontWeight: 'normal',
  wordBreak: 'break-word',
};

@Component({
  selector: 'fs-plate-map',
  templateUrl: './plate-map.component.html',
  styleUrls: ['./plate-map.component.scss'],
})
export class PlateMapComponent implements OnDestroy, OnChanges {
  @Input() public fields: any;
  @Input() public width: number;
  @Input() public drawingTools: boolean = false;
  @Input() public hideDrawings: boolean = false;
  @Input() public querySearchParams: boolean = false;
  @Input() public disablePolygon: boolean = false;
  @Input() public disableCircle: boolean = false;
  @Input() public disablePen: boolean = false;
  @Input() public disableRectangle: boolean = false;
  @Input() public results: IFsLprResult[] = [];
  @Input() public selectedPlate: IFsLprResult | null = null;
  @Input() public didQuerySearch: boolean = false;
  @Input() public isHistoricalView: boolean = false;
  @Input() public isLoadingHistoricalResults: boolean = false;
  @Input() public hideCameraMarkers: boolean = false;
  @Input() public hasPerformedValidSearch: boolean = false;
  @Output() public plateSelected = new EventEmitter<{
    selectedPlate: IFsLprResult;
    isHistoricalPlate: boolean;
  }>();
  @Output() public mapSearchTriggered = new EventEmitter<void>();
  @ViewChild(MatMenuTrigger) public trigger: MatMenuTrigger;

  public makes: string;
  public lprCameras = [];
  public snappedPolyline;
  public polyline: google.maps.Polyline;
  public deckGLoverlay: DeckOverlay;
  public breadcrumbsActive: boolean = false;
  public previousZoomLevel = 0;
  public hasActiveShape: boolean = false;

  private readonly _destroy$ = new Subject<void>();

  @Input()
  set plate(plate: SnPlate) {
    if (this._marker) {
      this._marker.remove();
    }
    this._plate = plate;
    this._closePopup();
    this._changeDetectorRef.detectChanges();
    if (!this._plate || !this._plate.geometry.coordinates) {
      return;
    }
    if (this._plates.length) {
      const existingPlate = this._plates.find((pl) => pl.id === this._plate.id);
      this._closePopup();
      const popup = new GoogleMapPopup(PlateInfoPopupComponent, {
        data: {
          plate: existingPlate,
          fields: this.fields,
        },
        lonLat: plate.geometry.coordinates,
      });
      this._selectedPopup = popup;
      this._selectedPopup.open(this.platesService.map);
    } else {
      if (this._plate) {
        if (!this.drawingTools) {
          this._marker = new SnPlateMarker(this._plate);
          this._marker.addTo(this.platesService.map);
        }
        this.platesService.map.setCenter(this._setLatLon(this._plate));
        const popup = new GoogleMapPopup(PlateInfoPopupComponent, {
          data: {
            plate: this._plate,
            fields: this.fields,
          },
          lonLat: this._plate.geometry.coordinates,
        });
        this._selectedPopup = popup;
        popup.open(this.platesService.map);
        this._changeDetectorRef.detectChanges();
      }
    }
  }

  get organization(): any {
    return this._currentOrganization.settings?.geometry?.coordinates;
  }

  get lprIntegrationTypes(): any[] {
    return this.lprIntegrationsService.integrations;
  }

  private _plate: any;
  private _marker: SnPlateMarker;
  private _plates: LprQueryResultsModel[] = [];
  private _mapLayer = new GoogleMapLayer<GoogleMapMarker>();
  private _selectedPopup: GoogleMapPopup;
  private _didMapInit: boolean = false;
  private _tooltipEl: HTMLDivElement | null = null;

  constructor(
    public readonly platesService: PlatesService,
    public readonly mapStoreService: MapStoreService,
    public readonly lprIntegrationsService: LprIntegrationsService,
    public readonly router: Router,
    private readonly _currentOrganization: SnCurrentOrganization,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _lprAPI: LprReadsAPI,
    private readonly _lprCamerasService: FsLprCamerasService,
    private readonly _httpClient: HttpClient,
    private readonly _mixpanelService: MixpanelService,
    private readonly _formatDatePipe: FsFormatDatePipe,
  ) {}

  @HostListener('window:keydown', ['$event'])
  public handleKeyDown(event: KeyboardEvent): void {
    if (this.drawingTools && event.code === 'Escape') {
      this.platesService.setDrawingMode();
    }
  }

  public ngOnDestroy(): void {
    this._cleanupTooltip();
    this._destroy$.next();
    this._destroy$.complete();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    // If already in historical view, update which historic dot is selected (green vs grey dots)
    if (changes.selectedPlate && this.isHistoricalView && !changes.isHistoricalView) {
      // Update the marker colors, but do not reset the zoom
      this._updateMarkers();
    }

    // Hide drawn shapes if the details drawer is open
    if (changes.hideDrawings) {
      this.platesService.toggleShapes(!this.hideDrawings);
    }

    // Ensure device markers remain visible after drawing operations
    if ((changes.drawingTools || changes.results) && !this.platesService.isDragging) {
      this._updateMarkers();
    }

    // Set up shape complete subscription when drawing tools are enabled
    if (changes.drawingTools && this.drawingTools && this.platesService.shapeComplete$) {
      this.platesService.shapeComplete$.pipe(takeUntil(this._destroy$)).subscribe(() => {
        // Update hasActiveShape based on service's activeShape
        this.hasActiveShape = !!this.platesService.activeShape;
        // Mark map interaction to re-enable button after drawing
        this.platesService.markMapInteraction();
        this._changeDetectorRef.markForCheck();
      });
    }
  }

  public isModeActive(mode: string): boolean {
    return this.platesService.drawingMode === mode;
  }
  public getSearchButtonText(): string {
    // If we have active shape but already searched it, show "Search this map"
    if (this.hasActiveShape && this.platesService.hasBoundarySearched) {
      return t('alpr.map.searchThisMap');
    }
    // Otherwise use existing logic
    return this.hasActiveShape ? t('alpr.map.searchThisBoundary') : t('alpr.map.searchThisMap');
  }

  public getSearchButtonTooltip(): string {
    // If we have active shape but already searched it, explain map view search
    if (this.hasActiveShape && this.platesService.hasBoundarySearched) {
      return t('alpr.map.searchMapViewTooltip');
    }
    // Otherwise use existing logic
    return this.hasActiveShape 
      ? t('alpr.map.searchBoundaryTooltip')
      : t('alpr.map.searchMapViewTooltip');
  }

  public isSearchButtonDisabled(): boolean {
    
    // Disable if no valid search has been performed yet
    if (!this.hasPerformedValidSearch) {
      return true;
    }
    
    // If no active shape and we've searched this view without subsequent interaction, disable
    if (!this.hasActiveShape && this.platesService.hasMapViewSearched && !this.platesService.mapInteracted) {
      return true;
    }
    
    return false;
  }

  public onSearchMapAreaClick(): void {
    this._mixpanelService.trackMetric(MxEventName.MapInteractionsLprSearchAf, {
      [MxPropertyName.MapAction]: this.hasActiveShape ? 'Search Area' : 'Search Map View',
    });
    
    if (this.hasActiveShape && !this.platesService.hasBoundarySearched) {
      // Only do boundary search if shape exists AND hasn't been searched yet
      this.platesService.hasBoundarySearched = true;
      this.mapSearchTriggered.emit();
    } else {
      // Do map view search (clear shape and search current view)
      this.platesService.setMapViewSearchParams();
    }
  }


  public onMapInit(googleMap: GoogleMap): void {
    this.platesService.onMapInit(googleMap);
    this._mapLayer.addTo(this.platesService.map);
    if (this.drawingTools) {
      this.platesService.initDrawingManager();
    }
    this.platesService.initShapeFromQueryParams();


    this.platesService.toggleShapes(!this.hideDrawings); // Hide shapes if the details drawer is open

    this.platesService.plates$.pipe(takeUntil(this._destroy$)).subscribe((plates) => {
      if (plates?.length) {
        this.lprCameras = [];
        this._updateMarkers();
        this.breadcrumbsActive = true;
        this._createBreadcrumbs(plates);
      } else {
        this.breadcrumbsActive = false;
        this._removeBreadcrumbs();
      }
    });
    this.platesService.clearMarkers$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this._mapLayer.clearOverlays();
      this._closePopup();
      this._plates = [];
      if (this.polyline) {
        this.polyline.setMap(null);
        this.polyline = null;
      }
      if (this.snappedPolyline) {
        this.snappedPolyline.setMap(null);
        this.snappedPolyline = null;
      }
    });

    if (this.querySearchParams) {
      this._patchQueriesForm();
    }

    this._createOverlay();
    // this.fitBounds(); // Removed to prevent map from zooming in on the historical results
    this._loadCameraMarkers();
    this.platesService.map
      .on('zoomChange')
      .pipe(takeUntil(this._destroy$))
      .subscribe((zoom) => {
        if (zoom !== this.previousZoomLevel) {
          const zoomDirection = zoom > this.previousZoomLevel ? 'IN' : 'OUT';
          this._mixpanelService.trackMetric(MxEventName.MapInteractionsLprSearchAf, {
            [MxPropertyName.MapAction]: `Zooms Map ${zoomDirection}`,
          });
        }
        this.previousZoomLevel = zoom;
          
        // Mark as interaction for search button
        this.platesService.markMapInteraction();
        this._changeDetectorRef.detectChanges();
      });

    // Listen for map drag interactions
    this.platesService.map
      .on('dragstart')
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.platesService.markMapInteraction();
        this._changeDetectorRef.detectChanges();
      });
    this.platesService.markerClicked$.pipe(takeUntil(this._destroy$)).subscribe((pickInfo) => {
      if (!pickInfo.object) return;

      if (pickInfo.layer.id === 'fs__lpr-webgl-marker') {
        this._handleLprCameraClick(pickInfo);
      } else if (
        ['vehicleScatterPlotLayer', 'historicalVehicleLayer'].includes(pickInfo.layer.id)
      ) {
        this._openPlateDetails(pickInfo.object);
      }
    });

    this.platesService.markerHovered$
      .pipe(takeUntil(this._destroy$))
      .subscribe(({ pickInfo, containerPoint }) => {
        this._handleTooltip(pickInfo, containerPoint);
      });

    // Subscribe to shape completion to ensure markers remain visible
    this.platesService.shapeComplete$.pipe(takeUntil(this._destroy$)).subscribe((shape) => {
      // Update hasActiveShape based on whether we have an active shape
      this.hasActiveShape = !!this.platesService.activeShape;
      
      // Mark as interaction when shape is completed/changed
      if (shape) {
        this.platesService.markMapInteraction();
      }
      
      this._changeDetectorRef.detectChanges();
      
      // Only update markers if not during a drag operation
      if (!this.platesService.isDragging) {
        this._updateMarkers();
      }
    });

    this._didMapInit = true;
  }

  public selectMakes(lprType: string): boolean {
    return this.platesService.activeMakes.includes(lprType);
  }

  public selectAllMakes(event: MatCheckboxChange): void {
    this.platesService.activeMakes = event.checked ? [...this.platesService.makesArray] : [];
    this._changeDetectorRef.detectChanges();
  }

  public updateLPRCameras(event: MatCheckboxChange, lprType: string): void {
    if (event.checked) {
      if (!this.platesService.activeMakes.includes(lprType)) {
        this.platesService.activeMakes.push(lprType);
      }
    }
    if (!event.checked) {
      const lprIndex = this.platesService.activeMakes.findIndex((make) => {
        return make === lprType;
      });

      this.platesService.activeMakes.splice(lprIndex, 1);
    }
  }

  public submitLPR(): void {
    this.platesService.lprMarkers.forEach((marker: FsLprCameraAdvancedMarker) => {
      marker.remove();
    });
    this.platesService.lprMarkers = [];
    localStorage.setItem('selectedLprMakes', JSON.stringify(this.platesService.activeMakes));
    this.trigger.closeMenu();
  }

  public highlightRoad(): void {
    if (this.snappedPolyline) {
      this.polyline.setVisible(true);
      this.snappedPolyline.setMap(null);
      this.snappedPolyline = null;
      return;
    }
    this.polyline.setVisible(false);
    const polyLine = this.polyline;
    for (let i = 0; i < polyLine.getPath().getArray().length; i += 100) {
      setTimeout(() => {
        const line = new google.maps.Polyline({
          path: polyLine
            .getPath()
            .getArray()
            .slice(i, i + 100),
          strokeOpacity: 1,
          strokeColor: THEME.success,
          strokeWeight: 1,
          draggable: false,
          icons: [
            {
              offset: '0',
              repeat: '100px',
            },
          ],
        });
        this._runSnapToRoad(line.getPath());
      }, 500 * (i / 100));
    }
  }

  /**
   * Fits the map to the bounds of the vehicle markers.
   */
  public fitBounds(): void {
    if (this.results.length > 0 && this.platesService.map) {
      this._setVehicleMarkersBounds();
      this._updateMarkers();
    }
  }

  private _runSnapToRoad(path: google.maps.MVCArray<google.maps.LatLng>): void {
    const pathValues = [];
    for (let i = 0; i < path.getLength(); i++) {
      pathValues.push(path.getAt(i).toUrlValue());
    }
    const params: any = {
      interpolate: true,
      key: GOOGLE_MAP_KEY,
      path: pathValues.join('|'),
    };
    this._httpClient
      .get('https://roads.googleapis.com/v1/snapToRoads', {
        params,
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe((response) => {
        this._processSnapToRoadResponse(response);
      });
  }

  private _processSnapToRoadResponse(data: any): void {
    const snappedCoordinates: google.maps.LatLng[] = [];
    const placeIdArray = [];
    for (let i = 0; i < data.snappedPoints.length; i++) {
      const latlng = new google.maps.LatLng(
        data.snappedPoints[i].location.latitude,
        data.snappedPoints[i].location.longitude,
      );
      snappedCoordinates.push(latlng);
      placeIdArray.push(data.snappedPoints[i].placeId);
    }
    this._drawSnappedPolyline(snappedCoordinates);
  }

  private _drawSnappedPolyline(snappedCoordinates: google.maps.LatLng[]): void {
    const lineSymbol = {
      path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
      fillOpacity: 1,
      strokeOpacity: 1,
      strokeWeight: 2,
      fillColor: '#fff',
      strokeColor: 'black',
      scale: 4,
    };
    this.snappedPolyline = new google.maps.Polyline({
      path: snappedCoordinates,
      strokeOpacity: 1,
      strokeColor: THEME.success,
      strokeWeight: 1,
      draggable: false,
      icons: [
        {
          icon: lineSymbol,
          offset: '0',
          repeat: '100px',
        },
      ],
    });
    this.snappedPolyline.setMap(this.platesService.map.mapInstance);
  }

  private _setLatLon(location: SnPlate): any {
    return {
      lng: location.geometry.coordinates[0],
      lat: location.geometry.coordinates[1],
    };
  }

  private _removeBreadcrumbs(): void {
    this._mapLayer.clearOverlays();
    this._closePopup();
    this._plates = [];
    if (this.polyline) {
      this.polyline.setMap(null);
    }
  }

  private _createBreadcrumbs(plates: any[]): void {
    this._mapLayer.clearOverlays();
    this._closePopup();
    this._plates = plates;
    if (this._marker) {
      this._marker.remove();
    }
    if (this.polyline) {
      this.polyline.setMap(null);
      this.polyline = null;
    }
    this.platesService.lprMarkers.forEach((marker) => {
      marker.remove();
    });
    const linePoints = plates.map(
      (plate) =>
        new google.maps.LatLng(plate.geometry.coordinates[1], plate.geometry.coordinates[0]),
    );
    const totalCount = plates.length;
    const plateResults = cloneDeep(this._plates);
    plateResults.reverse().forEach((plate: LprQueryResultsModel, index) => {
      const color = this._getMarkerColor(totalCount, index);
      const scale = index === totalCount - 1 ? 1.4 : 1;
      const marker = this._createMarker(plate, color, scale);
      this._mapLayer.addOverlay(marker);
      marker
        .on('click')
        .pipe(takeUntil(this._mapLayer.on('remove')), takeUntil(this._destroy$))
        .subscribe(() => {
          this._closePopup();
          this._openPopup(plate);
          this._changeDetectorRef.detectChanges();
        });
    });
    const lineSymbol = {
      path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
      fillOpacity: 1,
      strokeOpacity: 1,
      strokeWeight: 2,
      fillColor: '#fff',
      strokeColor: 'black',
      scale: 4,
    };

    this.polyline = new google.maps.Polyline({
      path: linePoints.reverse(),
      strokeOpacity: 1,
      strokeColor: THEME.success,
      strokeWeight: 1,
      draggable: false,
      icons: [
        {
          icon: lineSymbol,
          offset: '0',
          repeat: '100px',
        },
      ],
    });
    this.polyline.setMap(this.platesService.map.mapInstance);
    const bounds = this._getBounds(this.polyline);
    this.platesService.map.mapInstance.fitBounds(bounds);
  }

  private _getBounds(polyline: google.maps.Polyline): google.maps.LatLngBounds {
    const bounds = new google.maps.LatLngBounds();
    polyline.getPath().forEach((latLng) => {
      bounds.extend(latLng);
    });

    return bounds;
  }

  private _createMarker(
    plate: LprQueryResultsModel,
    color?: string,
    scale?: number,
  ): GoogleMapMarker {
    return new GoogleMapMarker({
      id: `plate-${plate.id}`,
      type: GoogleMapMarkerType.CIRCLE,
      lonLat: {
        lng: plate.geometry.coordinates[0],
        lat: plate.geometry.coordinates[1],
      },
      title: plate.plate,
      style: {
        scale: scale ? scale : 1,
        strokeOpacity: 1,
        fillOpacity: 1,
        fillColor: scale === 1 ? color : THEME.success,
        strokeWeight: scale === 0.3 ? 0.5 : 1,
      },
      icon: {
        fontSet: 'fa',
        fontIcon: 'fa-car',
      },
    });
  }

  private _getMarkerColor(totalCount: number, currentIndex: number): string {
    const greenColor = THEME.success;
    const desaturateAmount = Math.round(100 / (totalCount - 1));

    const desaturateBy = 100 - currentIndex * desaturateAmount;
    return tinycolor(greenColor).desaturate(desaturateBy).toRgbString();
  }

  private _processPoints(geometry: any, callback: any, thisArg: any): any {
    if (geometry instanceof google.maps.LatLng) {
      callback.call(thisArg, geometry);
    } else if (geometry instanceof google.maps.Data.Point) {
      callback.call(thisArg, geometry.get());
    } else {
      geometry.getArray().forEach((g) => {
        this._processPoints(g, callback, thisArg);
      });
    }
  }

  private _closePopup(): void {
    if (this._selectedPopup) {
      this._selectedPopup.close();
      this._selectedPopup = null;
    }
  }

  private _openPopup(plate: LprQueryResultsModel): void {
    const popup = new GoogleMapPopup(PlateInfoPopupComponent, {
      data: {
        plate,
        fields: this.fields,
        polyline: this.polyline,
      },
      lonLat: plate.geometry.coordinates,
    });
    this._selectedPopup = popup;
    this._selectedPopup.open(this.platesService.map);
  }

  private _loadCameraMarkers(): void {
    if (this.hideCameraMarkers) {
      return;
    }
    const flockEnabled = this._currentOrganization.enabledIntegrations.includes('flock');
    this.platesService.map
      .on('idle')
      .pipe(
        debounceTime(500),
        map(() => this.platesService.map.getBounds()),
        startWith(this.platesService.map.getBounds()),
        filter(() => !this.breadcrumbsActive),
        switchMap((lonLatBounds) => {
          this.lprCameras = [];
          const ne = lonLatBounds.getNorthWest();
          const sw = lonLatBounds.getSouthEast();
          let bbox;
          if (ne && sw) {
            bbox = `${sw.lon},${ne.lat},${ne.lon}, ${sw.lat}` || null;
          }

          return forkJoin([
            this._lprCamerasService.getCameraMap({ bbox }),
            flockEnabled ? this._lprAPI.getFlockCameraList({ in_bbox: bbox }) : of([]),
          ]);
        }),
        takeUntil(this._destroy$),
      )
      .pipe(takeUntil(this._destroy$), takeUntil(this._mapLayer.on('remove')))
      .subscribe((results) => {
        results.forEach((res) => {
          this.lprCameras.push(...res);
        });
        this._updateMarkers();
      });
  }

  private _updateMarkers(updatedPlateClicked: boolean = false): void {
    if (!this._didMapInit) {
      return;
    }

    try {
      this.platesService.lprMarkers = [];

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      this.lprCameras = this.lprCameras.filter((cam) => {
        const lastEvent = new Date(cam.properties?.last_event);
        return lastEvent.getTime() > thirtyDaysAgo.getTime();
      });

      const markers = new FsLprCameraWebGlMarkerLayer(this.lprCameras, {
        onClick: (info) => this._handleLprCameraClick(info),
      });
      const scatterplot = new FsLprCameraWebGlScatterPlotLayer(this.lprCameras, {
        onClick: (info) => this._handleLprCameraClick(info),
      });

      const layers = [scatterplot.getLayer(), markers.getLayer()];

      if (this.isHistoricalView && this.selectedPlate) {
        if (this.results.length > 0) {
          const historicalLayer = this._getHistoricalVehicleMarkersScatterPlotLayer();
          layers.push(historicalLayer);
        }
        const selectedPlateLayer = this._getSelectedPlateMarkerLayer();
        layers.push(selectedPlateLayer);
      } else if (this.results.length > 0) {
        const vehicleMarkersLayer = this._getVehicleMarkersScatterPlotLayer();
        layers.push(vehicleMarkersLayer);
      }
      if (updatedPlateClicked) {
        this._setVehicleMarkersBounds();
      }

      this._renderLayers(layers);
    } catch (error) {
      console.error('Error updating markers:', error);
    }
  }

  // DeckGL info is not typed
  private _handleLprCameraClick(info: any): void {
    if (!info.object) {
      return;
    }
    this._onLprQueryClick(new FsLprQueryCameraAdvancedMarker(info.object));
  }

  private _setVehicleMarkersBounds(): void {
    // Center the map on the points using fitBounds
    const bounds = new google.maps.LatLngBounds();
    const isValidPlateCoordinates =
      this.selectedPlate &&
      this.selectedPlate.geometry.coordinates[0] !== 0 &&
      this.selectedPlate.geometry.coordinates[1] !== 0;

    if (this.isHistoricalView) {
      // Collect all valid coordinates in one array
      const validCoordinates = [];

      if (isValidPlateCoordinates) {
        validCoordinates.push(
          new google.maps.LatLng(
            this.selectedPlate.geometry.coordinates[1],
            this.selectedPlate.geometry.coordinates[0],
          ),
        );
      }

      this.results.forEach((plate) => {
        if (plate.geometry.coordinates[0] !== 0 && plate.geometry.coordinates[1] !== 0) {
          validCoordinates.push(
            new google.maps.LatLng(plate.geometry.coordinates[1], plate.geometry.coordinates[0]),
          );
        }
      });

      // Extend bounds once with all valid coordinates
      validCoordinates.forEach((coordinate) => bounds.extend(coordinate));

      if (!bounds.isEmpty() && this.platesService.map) {
        this.platesService.map.mapInstance.fitBounds(bounds);

        // If we zoom too close to the plates, we need to zoom out a bit
        // (overall zoom is centered on the selected plate)
        const listener = this.platesService.map.mapInstance.addListener('idle', () => {
          const currentZoom = this.platesService.map.mapInstance.getZoom();
          const maxZoom = 17;
          if (currentZoom > maxZoom) {
            if (isValidPlateCoordinates) {
              this.platesService.map.mapInstance.setCenter(
                new google.maps.LatLng(
                  this.selectedPlate.geometry.coordinates[1],
                  this.selectedPlate.geometry.coordinates[0],
                ),
              );
            }
            this.platesService.map.mapInstance.setZoom(maxZoom);
          }
          listener.remove();
        });
      }
      return;
    } else {
      // Normal view: include all results
      this.results.forEach((plate) => {
        if (plate.geometry.coordinates[0] != 0 && plate.geometry.coordinates[1] != 0) {
          bounds.extend(
            new google.maps.LatLng(plate.geometry.coordinates[1], plate.geometry.coordinates[0]),
          );
        }
      });
    }

    if (!bounds.isEmpty() && this.platesService.map) {
      this.platesService.map.mapInstance.fitBounds(bounds);
    }
  }

  /**
   * Opens the fleet query plate details component with the plate selected.
   */
  private _openPlateDetails(plate: IFsLprResult): void {
    this.plateSelected.emit({ selectedPlate: plate, isHistoricalPlate: this.isHistoricalView });
  }

  private _createOverlay(): void {
    this.deckGLoverlay = new DeckOverlay({
      id: 'plates-map',
      style: {
        zIndex: 2000,
      },
    });
    this.platesService.deckGLoverlay = this.deckGLoverlay;
  }

  private _renderLayers(layers: Layer[]): void {
    this.deckGLoverlay.setProps({
      layers,
      glOptions: {
        preserveDrawingBuffer: true,
      },
      onHover: (info: any) => {
        this.platesService.map.setHoveredLayers('plates-map', !!info.object);
        const containerPoint = new google.maps.Point(info.x, info.y);
        this._handleTooltip(info, containerPoint);
      },
    });

    this.deckGLoverlay.setMap(this.platesService.map.mapInstance);
    this.platesService.deckGLoverlay = this.deckGLoverlay;
  }

  private _getTooltip(info: any): any {
    if (!info.object) return null;

    // Timestamp tooltip for selected plate and historical vehicle markers
    if (info.layer.id === 'selectedPlateLayer' || info.layer.id === 'historicalVehicleLayer') {
      const date = new Date(info.object.createdTimestamp);
      return `${this._formatDatePipe.transform(date, 'timeFirstNoAmPm')} - ${info.object.plate}`;
    }

    if (info.object.plate && info.object.createdTimestamp) {
      const date = new Date(info.object.createdTimestamp);
      return `${this._formatDatePipe.transform(date, 'timeFirstNoAmPm')} - ${info.object.plate}`;
    }
    return info.object.properties?.name || '';
  }

  private _onLprQueryClick(marker: FsLprQueryCameraAdvancedMarker): void {
    if (!marker.camera?.properties) {
      return;
    }
    const popup = new GoogleMapPopup(LprCameraDetailsPopupComponent, {
      data: marker,
      lonLat: marker.getLonLat(),
      disableAutoPan: true,
      pixelOffset: new google.maps.Size(0, -12, 'pixel', 'pixel'),
    });
    popup.open(this.platesService.map);
    if (this._selectedPopup) {
      this._selectedPopup.close();
    }
    this._selectedPopup = popup;

    popup.componentInstance.markerUpdated$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this._loadCameraMarkers();
    });
  }

  private _patchQueriesForm(): void {
    this.platesService.patchQueriesForm();
  }

  private _getVehicleMarkersScatterPlotLayer(): ScatterplotLayer {
    return new ScatterplotLayer({
      id: 'vehicleScatterPlotLayer',
      data: this.results,
      opacity: 1,
      stroked: true,
      filled: true,
      radiusScale: 2,
      radiusMinPixels: 10,
      radiusMaxPixels: 10,
      pickable: true,
      lineWidthMinPixels: 2,
      lineWidthMaxPixels: 2,
      getFillColor: [102, 189, 77],
      onClick: (data) => this._openPlateDetails(data.object),
      getPosition: (d) => [d.geometry.coordinates[0], d.geometry.coordinates[1]],
      getRadius: () => 10,
    });
  }

  private _getSelectedPlateMarkerLayer(): ScatterplotLayer {
    return new ScatterplotLayer({
      id: 'selectedPlateLayer',
      data: [this.selectedPlate],
      opacity: 1,
      stroked: true,
      filled: true,
      radiusScale: 2,
      radiusMinPixels: 12, // Slightly larger than other markers
      radiusMaxPixels: 12,
      pickable: true,
      lineWidthMinPixels: 2,
      lineWidthMaxPixels: 2,
      getFillColor: [0, 255, 0], // Green color
      getPosition: (d) => [d.geometry.coordinates[0], d.geometry.coordinates[1]],
      getRadius: () => 12,
    });
  }

  private _getHistoricalVehicleMarkersScatterPlotLayer(): ScatterplotLayer {
    return new ScatterplotLayer({
      id: 'historicalVehicleLayer',
      data: this.results,
      opacity: 1,
      stroked: true,
      filled: true,
      radiusScale: 2,
      radiusMinPixels: 8,
      radiusMaxPixels: 8,
      pickable: true,
      lineWidthMinPixels: 2,
      lineWidthMaxPixels: 2,
      getFillColor: [169, 169, 169], // Gray color for historical markers
      onClick: (data) => this._openPlateDetails(data.object),
      getPosition: (d) => [d.geometry.coordinates[0], d.geometry.coordinates[1]],
      getRadius: () => 8,
    });
  }

  public get isUnknownLocation(): boolean {
    const historicalViewPointUnknown =
      this.isHistoricalView &&
      this.selectedPlate &&
      this.selectedPlate.geometry.coordinates[0] === 0 &&
      this.selectedPlate.geometry.coordinates[1] === 0;

    // The map should be interactable before we query search
    const plateResultPointsUnknown =
      this.didQuerySearch &&
      this.results.every(
        (plate) => plate.geometry.coordinates[0] === 0 && plate.geometry.coordinates[1] === 0,
      );
    return historicalViewPointUnknown && plateResultPointsUnknown;
  }

  public resetMapState(): void {
    // Clear popup and map overlays
    this._closePopup();
    this._mapLayer.clearOverlays();

    this._changeDetectorRef.detectChanges();
  }

  private _createTooltipElement(): HTMLDivElement {
    const tooltipEl = document.createElement('div');
    Object.assign(tooltipEl.style, TOOLTIP_STYLES);
    return tooltipEl;
  }

  private _positionTooltip(
    pickInfo: any,
    tooltipEl: HTMLDivElement,
    containerPoint: google.maps.Point,
  ): void {
    if (!pickInfo?.object?.geometry?.coordinates) return;

    this.platesService.projectionOverlay.setMap(this.platesService.map.mapInstance);
    const latLng = new google.maps.LatLng(
      pickInfo.object.geometry.coordinates[1],
      pickInfo.object.geometry.coordinates[0],
    );
    if (!latLng) return;

    const mapDiv = this.platesService.map.mapInstance.getDiv();
    const mapRect = mapDiv.getBoundingClientRect();

    tooltipEl.style.left = `${mapRect.left + containerPoint.x}px`;
    tooltipEl.style.top = `${mapRect.top + containerPoint.y}px`;
  }

  private _handleTooltip(info: any, containerPoint?: google.maps.Point): void {
    this._cleanupTooltip();

    if (info?.object) {
      if (!this._tooltipEl) {
        this._tooltipEl = this._createTooltipElement();
        document.body.appendChild(this._tooltipEl);
      }
      this._positionTooltip(info, this._tooltipEl, containerPoint);
      this._tooltipEl.innerHTML = this._getTooltip(info);
    }
  }

  private _cleanupTooltip(): void {
    if (this._tooltipEl) {
      this._tooltipEl.remove();
      this._tooltipEl = null;
    }
  }
}
